import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BoardSection from '../Board/BoardSection'
import Hand from '../Hand/Hand'
import './OpponentSection.scss'
import Card from '../Card/Card'
import { SECTIONS, defaultSections } from '../PlayerSection/PlayerSection'
import Deck from '../Deck/Deck'
import Discard, { OpponentDiscard } from '../Discard/Discard'
import { ACTION_MODES } from '../ActionMode'
import { v4 as uuidv4 } from 'uuid';
import { useGameContext } from '../../Commons/GameContext'
import LayoutBoardSection from '../Board/LayoutBoardSection'
import { Button } from 'react-bootstrap'
import { isValidUrl, setPositions } from '../../Commons/Functions'
import HiddenSectionModal from './HiddenSectionModal'

export function OpponentSection(props) {
    const [deck, setDeck] = useState([])
    const [visibleCards, setVisibleCards] = useState([])
    const visibleCardsRef = useRef([])

    const hasBeenInitializedRef = useRef(false) // TMP

    const cardDraggedRef = useRef(false)
    const handRef = useRef()
    const deckRef = useRef()
    const discardRef = useRef()
    const opponentSectionRef = useRef()

    const [sections, setSections] = useState(false)
    const sectionsCardsRef = useRef({})
    const { cardSize, gameData } = useGameContext()
    const [isZoomed, setIsZoomed] = useState(false)
    const scaleRatio = useRef(1)
    const [isResizing, setIsResizing] = useState(false)

    const refreshCount = useRef(0)

    function updateSectionsClientRect() {
        if (!sections || !opponentSectionRef.current) return;

        const element = opponentSectionRef.current;

        // 🧠 Sauve le transform actuel, appliqué par React
        const originalTransform = element.style.transform || window.getComputedStyle(element).transform;

        // 💣 Écrase TOUT transform (y compris ceux injectés via stylePlayArea)
        element.style.setProperty('transform', 'none', 'important');

        // Force un reflow pour appliquer le changement
        element.classList.add('disable-transform');
        void element.offsetHeight; // force reflow
        // → mesure ici
        element.classList.remove('disable-transform');

        const newSections = { ...sections };
        delete newSections.shouldUpdateClientRect;
        delete sections.shouldUpdateClientRect;

        newSections.allSections.forEach((s) => {
            const ref = newSections.sectionsDict[s].sectionRef.current;
            if (ref) {
                const rect = ref.getBoundingClientRect();
                newSections.sectionsDict[s].sectionBoundingClientRect = rect;
                sections.sectionsDict[s].sectionBoundingClientRect = rect;
            }
        });

        setSections(newSections);

        // ✅ Restaure le transform d’origine
        element.style.setProperty('transform', originalTransform, 'important');

        // 🔄 Relance le positionnement
        repositionCards(false, true);

        setIsResizing(true)
        setTimeout(() => {
            setIsResizing(false)
        }, 700)
    }

    useEffect(() => {
        updateScaleRatio()
        updateSectionsClientRect()
    }, [cardSize])

    useEffect(() => {
        if (sections.shouldUpdateClientRect) {
            updateSectionsClientRect()
        }
    }, [sections])

    function updateScaleRatio() {
        const refRatio = 2.08922; // format de base du plateau
        const currentRatio = window.innerWidth / window.innerHeight;
        //console.log(currentRatio)
        /*
                if (currentRatio > refRatio) {
                    // Ecran + large que 16:9 : on limite par la hauteur
                    scaleRatio.current = currentRatio / refRatio;
                } else {
                    // Ecran + étroit que 16:9 : on limite par la largeur
                    scaleRatio.current = refRatio / currentRatio;
                }*/
        const newRatio = refRatio / currentRatio
        scaleRatio.current = newRatio
        //console.log("new ratio " + scaleRatio.current)
    }

    useEffect(() => {
        if (props.gameOptions) {
            const sectionsFile = props.gameOptions.format.sections

            // SETUP SECTIONS
            let newSections = {
                allSections: [...sectionsFile.customSections, ...defaultSections.sections],
                customSections: [...sectionsFile.customSections],
                layout: { ...sectionsFile.layout },
                autoPlayFromHand: sectionsFile.autoPlayFromHand,
                autoPlayFromStack: sectionsFile.autoPlayFromStack,
                sectionsDict: {},
            }
            sectionsFile.sectionsDict = { ...defaultSections.sectionsDict, ...sectionsFile.sectionsDict }
            newSections.allSections.forEach((key) => {
                let section = sectionsFile.sectionsDict[key]
                let ref = null

                if (section.isDefaultSection) {
                    if (key == SECTIONS.HAND) {
                        ref = handRef
                    } else if (key == SECTIONS.DISCARD || key == SECTIONS.EXILE || key == SECTIONS.EXILE_HIDDEN || key == SECTIONS.SIDEBOARD) {
                        ref = discardRef
                    } else if (key == SECTIONS.DECK) {
                        ref = deckRef
                    } else if (key == SECTIONS.STACK) {
                        ref = props.stackRef
                    }
                } else {
                    ref = createRef()
                }

                newSections.sectionsDict[key] = {
                    title: section.title,
                    isHidden: section.isHidden,
                    sectionRef: ref,
                    sectionBoundingClientRect: false,
                    height: SECTIONS.SECTION_SIZES[section.height],
                    alignment: section.opponentAlignment ? SECTIONS.ALIGNMENTS[section.opponentAlignment] : SECTIONS.ALIGNMENTS[section.alignment],
                    cardHeight: SECTIONS.SECTION_SIZES[section.height],
                    cardWidth: SECTIONS.SECTION_SIZES[section.height] * 0.7,
                    autoPayFrom: section.autoPayFrom,
                    noAutoPayTo: section.noAutoPayTo,
                    isHorizontalAllowed: section.isHorizontalAllowed,
                    isGroupForbidden: section.isGroupForbidden,
                    keepTappedNewTurn: section.keepTappedNewTurn
                }
            })
            newSections.shouldUpdateClientRect = true
            setSections(newSections)
        }
    }, [props.gameOptions])

    useEffect(() => {
        let newCards = props.opponentData.visibleCards
        visibleCardsRef.current = newCards
        setDeck([...props.opponentData.deck])
        repositionCards()
    }, [props.opponentData])

    let repositionScheduled = false;
    let repositionHandle = null;

    const { stylePlayArea, styleBackground } = useMemo(() => {
        return getOpponentStyles(
            props.placeInDisplayOrder,
            props.opponentsTotal,
            props.currentlyZoomedPlayer,
            scaleRatio.current
        );
    }, [props.placeInDisplayOrder, props.opponentsTotal, props.currentlyZoomedPlayer, scaleRatio.current]);


    // Reposition all cards in visibleCards
    function repositionCards() {
        if (repositionScheduled) return;

        const run = () => {
            repositionScheduled = false;
            repositionHandle = null;

            if (!sections) return;

            const vh = cardSize.vh;

            sectionsCardsRef.current = {};
            for (const s of sections.allSections) {
                sectionsCardsRef.current[s] = [];
            }

            const newVisibleCards = [...visibleCardsRef.current];
            newVisibleCards.forEach((card, index) => {
                const sec = card.position.section;
                if (sec && sectionsCardsRef.current[sec]) {
                    sectionsCardsRef.current[sec].push({ card, index });
                }
            });

            function setPositions(cardsInSection, sectionRect, alignment) {
                if (!sectionRect || !cardsInSection.length) return;

                for (const data of cardsInSection) {
                    if (data.card.position.index === undefined) {
                        data.card.position.index = 99999;
                    }
                }

                cardsInSection.sort((a, b) => a.card.position.index - b.card.position.index);

                let totalWidth = 0;
                for (const { card } of cardsInSection) {
                    if (card.isTriggerEffect) continue;

                    totalWidth += (card.isHorizontal && card.isTapped)
                        ? card.position.cardSize.height
                        : card.position.cardSize.width;

                    if (card.grouppedCards) {
                        totalWidth += card.grouppedCards.length * (card.position.cardSize.width / 2);
                    }
                }
                totalWidth *= vh;

                let alignmentOffset = 0;
                if (alignment === SECTIONS.ALIGNMENTS.CENTER) {
                    alignmentOffset = sectionRect.width / 2 - Math.min(totalWidth, sectionRect.width) / 2;
                } else if (alignment === SECTIONS.ALIGNMENTS.END) {
                    alignmentOffset = sectionRect.width;
                }

                const shrinkOffset = totalWidth > sectionRect.width
                    ? (totalWidth - sectionRect.width) / (cardsInSection.length - 1)
                    : 0;

                let cumulatedWidth = 0;
                for (let i = 0; i < cardsInSection.length; i++) {
                    const { card, index: visibleCardIndex } = cardsInSection[i];
                    const cardWidth = (card.isHorizontal && card.isTapped)
                        ? card.position.cardSize.height * vh
                        : card.position.cardSize.width * vh;

                    const groupSpacing = (card.position.cardSize.width / 2) * vh;
                    const childWidth = card.grouppedCards
                        ? card.grouppedCards.length * groupSpacing
                        : 0;

                    card.position.index = i;
                    card.position.reversedIndex = cardsInSection.length - i - 1;

                    let left = sectionRect.left;
                    if (alignment === SECTIONS.ALIGNMENTS.NONE) {
                        // no adjustment
                    } else if (alignment === SECTIONS.ALIGNMENTS.END) {
                        left += alignmentOffset - cumulatedWidth + (i > 0 ? shrinkOffset * i : 0) - childWidth - cardWidth;
                    } else {
                        left += cumulatedWidth + alignmentOffset - (i > 0 ? shrinkOffset * i : 0) + childWidth;
                    }

                    card.position.pos = { left, top: sectionRect.top };

                    if (card.grouppedCards) {
                        const groupTop = sectionRect.top + card.position.cardSize.height * vh * 0.1;
                        card.grouppedCards.forEach((child, j) => {
                            const childIndex = newVisibleCards.findIndex(cv => cv.id === child.id);
                            if (childIndex === -1) return;

                            const childCard = newVisibleCards[childIndex];
                            childCard.position.pos = {
                                left: alignment === SECTIONS.ALIGNMENTS.END
                                    ? left + groupSpacing * (j + 1)
                                    : left - groupSpacing * (j + 1),
                                top: groupTop
                            };
                            childCard.position.index = i - 10 - j;
                        });
                    }

                    if (!card.isTriggerEffect) {
                        cumulatedWidth += cardWidth + childWidth;
                    }
                }
            }

            for (const s of sections.allSections) {
                if (s !== SECTIONS.STACK) {
                    const sec = sections.sectionsDict[s];
                    setPositions(sectionsCardsRef.current[s], sec.sectionBoundingClientRect, sec.alignment);
                }
            }

            setSections(sections);
            setVisibleCards(newVisibleCards);
            visibleCards.current = newVisibleCards;
            refreshCount.current = refreshCount.current > 1000 ? 1 : refreshCount.current + 1
        };

        // Schedule run on idle time or animation frame
        repositionScheduled = true;
        if ('requestIdleCallback' in window) {
            repositionHandle = requestIdleCallback(run, { timeout: 100 });
        } else {
            repositionHandle = requestAnimationFrame(run);
        }
    }

    function moveCard() {
        console.log("fuck, moving card")
    }

    if (!sections) {
        return (
            <div className={"opponent-section d-flex flex-column w-100 h-100 position-absolute top-0 start-0 pe-none unzoomed" + (props.isCurrentPlayer ? " current-player" : "")}>
                {props.profileData && (
                    <div
                        id={"playmat-" + props.opponentId}
                        className="background position-absolute"
                        style={
                            isValidUrl(props.profileData.background)
                                ? { ...styleBackground, backgroundImage: `url(${props.profileData.background})` }
                                : { ...styleBackground, backgroundColor: props.profileData.background }
                        }
                    ></div>
                )}
            </div>
        )
    }

    return (
        <div className={"opponent-section d-flex flex-column w-100 h-100 position-absolute top-0 start-0 pe-none" + (props.isCurrentPlayer ? " current-player" : "") + (props.currentlyZoomedPlayer >= 0 ? " has-zoomed" : " no-zoomed") + (props.currentlyZoomedPlayer === props.placeInDisplayOrder ? " zoomed" : " unzoomed") + " opponent-order-" + (props.placeInDisplayOrder) + (props.opponentData.isEliminated ? " eliminated" : "")} style={{ zIndex: 10 + props.placeInDisplayOrder }}>
            {props.profileData && (
                <div
                    id={"playmat-" + props.opponentId}
                    className="background position-absolute"
                    style={
                        isValidUrl(props.profileData.background)
                            ? { ...styleBackground, backgroundImage: `url(${props.profileData.background})` }
                            : { ...styleBackground, backgroundColor: props.profileData.background }
                    }
                ></div>
            )}
            <div className={'pe-auto d-flex flex-column position-relative w-100 h-100' + (isResizing ? " opacity-0" : "")} ref={opponentSectionRef} style={stylePlayArea}>
                <div className="position-relative d-flex flex-column w-100 h-100 justify-content-after">
                    <div style={{ height: '38vh' }}>
                        <LayoutBoardSection
                            sectionLayout={sections.layout}
                            cardDraggedRef={cardDraggedRef}
                            moveCard={moveCard}
                            sections={sections}
                            sectionsCardsRef={sectionsCardsRef}
                            repositionCards={repositionCards}
                            isOpponentSection={true}
                        />
                    </div>
                    <Hand cardDraggedRef={cardDraggedRef} moveCard={moveCard} handRef={handRef} repositionCards={repositionCards} />
                    <Deck
                        deck={deck}
                        deckRef={deckRef}
                        draw={() => console.log("fuck draw")}
                        repositionCards={repositionCards}
                        playerId={props.playerId}
                        topCardHiddenTo={props.opponentData.deckTopCardHiddenTo}
                        setFocusedCard={props.setFocusedCard}
                        currentActionMode={{ keys: {} }}
                    />
                    <OpponentDiscard
                        cardDraggedRef={cardDraggedRef}
                        discardRef={discardRef}
                        moveCard={moveCard}
                        repositionCards={repositionCards}
                        visibleCards={visibleCards}
                        sectionCards={sectionsCardsRef.current[SECTIONS.DISCARD]}
                        sendToHistory={props.sendToHistory}
                        refreshCount={props.refreshCount}
                    />
                </div>
                <div className='visible-cards position-fixed start-0 top-0 w-100 h-100 pe-none'>
                    {visibleCards.map((c, i) => {
                        return (<Card
                            key={c.id}
                            card={c}
                            cardDraggedRef={cardDraggedRef}
                            clickCard={() => {
                                if (props.currentActionMode.action === ACTION_MODES.PING) {
                                    props.pingCard(c)
                                } else if (props.currentActionMode.action == ACTION_MODES.LINK_FROM || props.currentActionMode.action == ACTION_MODES.LINK_TO) {
                                    if (!props.currentActionMode.actionData) {
                                        props.setCurrentActionMode({
                                            ...props.currentActionMode,
                                            action: ACTION_MODES.LINK_TO,
                                            actionData: {
                                                id: uuidv4(),
                                                fromCardName: c.cardData.name,
                                                fromSection: c.position.section,
                                                from: c.id,
                                                to: false,
                                                toSection: false
                                            }
                                        })
                                    } else {
                                        let linkData = props.currentActionMode.actionData
                                        linkData.to = c.id
                                        linkData.toSection = c.position.section
                                        let tmp = [...props.cardsLinks]
                                        tmp.push(linkData)
                                        props.setCardsLinks(tmp)
                                        if (linkData.isFromQuickAction) {
                                            props.setCurrentActionMode({
                                                ...props.currentActionMode,
                                                action: ACTION_MODES.NONE,
                                                actionData: false
                                            })
                                        } else {
                                            props.setCurrentActionMode({
                                                ...props.currentActionMode,
                                                action: ACTION_MODES.LINK_FROM,
                                                actionData: false
                                            })
                                        }
                                    }
                                } else if (props.currentActionMode.action == ACTION_MODES.GROUP) {
                                    // GIVE CARD
                                    if (c.grouppedToId || c.position.section === SECTIONS.HAND || sections.sectionsDict[c.position.section].alignment === SECTIONS.ALIGNMENTS.DECK) { return }
                                    let tmp = { ...props.currentActionMode.actionData }
                                    tmp.grouppingTarget = c.id
                                    tmp.opponentId = props.opponentId
                                    props.setCurrentActionMode({
                                        ...props.currentActionMode,
                                        action: ACTION_MODES.GROUPPING_TO_OPPONENT,
                                        actionData: tmp
                                    })
                                } else if (props.currentActionMode.action == ACTION_MODES.NONE) {
                                    // Default action
                                    props.pingCard(c)
                                }
                            }}
                            deck={deck}
                            cards={visibleCards}
                            index={i}
                            repositionCards={repositionCards}
                            setCurrentActionMode={() => console.log("fuck")}
                            setFocusedCard={props.setFocusedCard}
                            isOpponentCard={true}
                            interactionFobidden={true}
                            currentActionMode={props.currentActionMode}
                            playerId={props.playerId}
                            handSize={sectionsCardsRef.current[SECTIONS.HAND].length}
                            refreshCount={refreshCount.current}
                            sectionData={sections.sectionsDict[c.position.section]}
                        />)
                    })}
                </div>
            </div>
            {/*<Button className='position-absolute bottom-0 start-50 pe-auto translate-x-middle'
                style={{ zIndex: "50000", scale: 2 }}
                onClick={() => {
                    setIsZoomed(!isZoomed)
                }}
            >{isZoomed ? "Unzoom" : "Zoom"}</Button>*/}
        </div>
    )
}

function areEqual(prevProps, nextProps) {
    return prevProps.opponentData.refreshCount === nextProps.opponentData.refreshCount
        && prevProps.isCurrentPlayer === nextProps.isCurrentPlayer
        && prevProps.currentActionMode.actionData === nextProps.currentActionMode.actionData
        && prevProps.currentlyZoomedPlayer === nextProps.currentlyZoomedPlayer
}

export default React.memo(OpponentSection, areEqual);

export function getOpponentStyles(playerOrder, opponentsTotal, currentlyZoomed, scaleRatio) {
    if (opponentsTotal == 1) {
        return ({
            stylePlayArea: {
                transform: "rotate(180deg)"
            },
            styleBackground: {
                top: 0,
                left: 0,
                width: "calc(100% + 32vh - var(--playmat-border) * 2)",
                height: "calc(100% - var(--playmat-border))"
            }
        })
    }
    const baseScale = 0.25;
    const zoomedWidth = "64%"
    const playArea = {};
    const background = {};
    const miniDislayOffsetY = opponentsTotal === 3 ? 31 : 22
    const boardHeight = `calc((100% - var(--playmat-border)) / ${opponentsTotal})`
    playArea.transform = `rotate(180deg) scale(${baseScale / scaleRatio})`



    // Taille sur le coté quand un autre joueur est zoomé
    let playAreaTranslateY = `calc(${boardHeight} * ${playerOrder} - ${miniDislayOffsetY}% + var(--playmat-border) * 2.5 * ${playerOrder})`;
    let playAreaTranslateX = applyStrength(46.5, scaleRatio, 0.45) + "%"
    let playAreaScale = baseScale
    let playAreaRotate = "180deg"

    background.top = `calc((${boardHeight} + var(--playmat-border)) * ${playerOrder} - var(--playmat-border))`;
    background.left = `calc(${zoomedWidth} + 32vh + var(--playmat-border) * 2)`
    background.width = `calc(100% - var(--playmat-border) * 4 - ${zoomedWidth})`;
    background.height = `calc(${boardHeight} - var(--playmat-border))`

    // Le joueur zoomé
    if (currentlyZoomed === playerOrder) {
        playAreaScale = applyStrength(0.82, scaleRatio, 0.8)
        playAreaTranslateY = `7%`;
        playAreaTranslateX = "-" + applyStrength(8.5, scaleRatio, 2.4) + "%"

        background.top = 0
        background.left = 0
        background.width = `calc(${zoomedWidth} + 32vh)`
        background.height = `calc(100% - var(--playmat-border))`
    }
    // Si personne n'est zoomé
    else if (currentlyZoomed === -1) {
        playAreaTranslateY = 0;
        background.top = 0

        let width = "33%"
        if (opponentsTotal === 3) {
            const sideAngle = 210 - 65 * scaleRatio
            playAreaRotate = `${sideAngle + playerOrder * (180 - sideAngle)}deg`
            playAreaScale = 0.32
            playAreaTranslateX = `calc((${width} + var(--playmat-border) * 2) * ${playerOrder - 1})`

            background.left = `calc((${width} + var(--playmat-border) * 2) * ${playerOrder})`

            if (playerOrder === 2) {
                width = `calc(${width} + 32vh + var(--playmat-border))`
            } else if (playerOrder === 1) {
                playAreaTranslateY = "-10%";
            }
            background.width = width
            background.height = `calc(100% - var(--playmat-border))`
        } else if (opponentsTotal === 2) {
            width = "50%"
            playAreaScale = applyStrength(0.50, scaleRatio, 1.1)
            playAreaTranslateX = `calc(${width} * ${playerOrder - 0.5} + ${playerOrder} * var(--playmat-border) * 6)`

            background.left = `calc((${width} + var(--playmat-border) * 2) * ${playerOrder})`

            if (playerOrder === 1) {
                width = `calc(${width} + 32vh - var(--playmat-border) * 4)`
            }
            background.width = width
            background.height = `calc(100% - var(--playmat-border))`
        }
    }

    playArea.transform = `translateX(${playAreaTranslateX}) translateY(${playAreaTranslateY}) rotate(${playAreaRotate}) scale(${applyStrength(playAreaScale, scaleRatio, 0.88)})`

    return {
        stylePlayArea: playArea,
        styleBackground: background,
    };
}

function applyStrength(value, scaleRatio, factor = 1) {
    return value * (scaleRatio + (1 - scaleRatio) * factor);
}
